import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AccessEnum, UserMaterializedPermissionsDto } from "~/api/GeneratedApi";
import {
  Accesses,
  AccessKeyRecord,
  UserAccess,
} from "~/components/AccessWrapper";
import { User } from "~/utils/hooks/useAuth";
import { RootState } from "../store/store";

// Define a type for the slice state
interface AuthState {
  user: User | null;
  accesses: UserMaterializedPermissionsDto | null;
  keys: AccessKeyRecord;
  isGlobalAdmin: boolean;
  transformedAccesses: Accesses | null;
  anyAccess: UserAccess | null;
  accessesSet: boolean;
}

// Define the initial state using that type
const initialState: AuthState = {
  user: null,
  accesses: null,
  keys: Object.keys(AccessEnum).reduce(
    (prev, cur) => ({
      ...prev,
      [AccessEnum[cur as keyof typeof AccessEnum]]: cur,
    }),
    {},
  ) as AccessKeyRecord,
  isGlobalAdmin: false,
  transformedAccesses: null,
  anyAccess: null,
  accessesSet: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
    },
    setAccesses: (
      state,
      action: PayloadAction<UserMaterializedPermissionsDto>,
    ) => {
      state.accesses = action.payload;
    },
    setIsGlobalAdmin: (state, action: PayloadAction<boolean>) => {
      state.isGlobalAdmin = action.payload;
    },
    setTransformedAccesses: (state, action: PayloadAction<Accesses>) => {
      state.transformedAccesses = action.payload;
    },
    setKeys: (state, action: PayloadAction<AccessKeyRecord>) => {
      state.keys = action.payload;
    },
    setAnyAccess: (state, action: PayloadAction<UserAccess>) => {
      state.anyAccess = action.payload;
    },
    setAccessesSet: (state, action: PayloadAction<boolean>) => {
      state.accessesSet = action.payload;
    },
  },
});

export const {
  setUser,
  setAccesses,
  setAccessesSet,
  setAnyAccess,
  setIsGlobalAdmin,
  setKeys,
  setTransformedAccesses,
} = authSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectUser = (state: RootState): User | null => state.auth.user;
export const selectAccesses = (state: RootState) => state.auth.accesses;
export const selectAccessesSet = (state: RootState) => state.auth.accessesSet;
export const selectTransformedAccesses = (state: RootState) =>
  state.auth.transformedAccesses;
export const selectAnyAccess = (state: RootState) => state.auth.anyAccess;
export const selectIsGlobalAdmin = (state: RootState) =>
  state.auth.isGlobalAdmin;
export const selectKeys = (state: RootState) => state.auth.keys;

export default authSlice.reducer;
